import { Link } from "react-router-dom";
import { Lock } from "lucide-react";
import { uploadUrl } from "@/lib/api";
import { fmtCAD } from "@/lib/currency";
import { variantName, variantQty, priceRange } from "@/lib/variants";

const FALLBACK_IMG =
  "https://images.unsplash.com/photo-1622290291468-a28f7a7dc6a8?auto=format&fit=crop&w=900&q=80";

export default function ProductCard({ product }) {
  const img = uploadUrl(product.images?.[0]) || FALLBACK_IMG;
  const showWholesale = product.wholesale_price != null;
  const sizes = product.sizes || [];
  const sizeNames = sizes.map(variantName).filter(Boolean);
  const range = priceRange(product);
  const wholesaleLabel = range.hasRange
    ? `${fmtCAD(range.min)}–${fmtCAD(range.max)}`
    : fmtCAD(product.wholesale_price);
  // In-stock product is sold out when every tracked variant is at 0.
  const trackedQtys = product.product_type === "in_stock"
    ? sizes.map(variantQty).filter((q) => q != null)
    : [];
  const soldOut = trackedQtys.length > 0 && trackedQtys.every((q) => q <= 0);

  return (
    <Link
      to={`/product/${product.id}`}
      data-testid={`product-card-${product.id}`}
      className="group block bg-white border border-border hover:border-foreground/30 transition-colors rounded-md overflow-hidden"
    >
      {/* Block layout on the Link + padding-bottom:100% on this container
          guarantees the image area is a true square. flex-col was letting
          CSS Grid row-stretching squish the image at wide viewports. */}
      <div className="relative w-full overflow-hidden bg-secondary" style={{ paddingBottom: "100%" }}>
        <img
          src={img}
          alt={product.name}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-contain transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute top-2 left-2 px-2 py-0.5 text-[10px] uppercase tracking-[0.1em] bg-white/90 border border-border rounded text-foreground/80">
          {product.category}
        </div>
        {soldOut && (
          <div
            data-testid={`card-soldout-${product.id}`}
            className="absolute top-2 right-2 px-2 py-0.5 text-[10px] uppercase tracking-[0.1em] bg-destructive text-white rounded"
          >
            Sold out
          </div>
        )}
      </div>
      <div className="p-3">
        <h3 className="text-sm font-medium truncate">{product.name}</h3>
        <div className="mt-1.5">
          {showWholesale ? (
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[11px] tabular-nums">
              <span className="uppercase tracking-[0.08em] text-muted-foreground">MSRP</span>
              <span className="text-foreground/80">{fmtCAD(product.msrp)}</span>
              <span className="text-muted-foreground/50">·</span>
              <span className="uppercase tracking-[0.08em] text-muted-foreground">Wholesale</span>
              <span
                data-testid={`card-wholesale-${product.id}`}
                className="font-semibold text-foreground"
              >{wholesaleLabel}</span>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 min-w-0 text-[12px]">
              <Lock size={11} strokeWidth={1.5} className="text-muted-foreground shrink-0" />
              <span data-testid={`card-msrp-${product.id}`} className="text-muted-foreground tabular-nums">
                <span className="uppercase tracking-[0.08em] mr-1">MSRP</span>{fmtCAD(product.msrp)}
              </span>
            </div>
          )}
        </div>
        {sizeNames.length > 0 && (
          <p className="mt-1 text-[11px] text-muted-foreground truncate">
            {sizeNames.slice(0, 4).join(" · ")}{sizeNames.length > 4 ? ` +${sizeNames.length - 4}` : ""}
          </p>
        )}
        {product.product_type === "in_stock" ? (
          <p className="mt-2 text-[9px] font-medium uppercase tracking-[0.06em] text-destructive/85 tabular-nums">
            In stock · ships immediately
          </p>
        ) : (
          <div className="mt-2 space-y-0.5">
            <p className="text-[9px] font-medium uppercase tracking-[0.06em] text-destructive/85 tabular-nums">
              Estimated ship window{product.delivery_window ? ` · ${product.delivery_window}` : ""}
            </p>
            {product.order_by_date && (
              <p className="text-[9px] font-medium uppercase tracking-[0.06em] text-destructive/85 tabular-nums">
                Order by {product.order_by_date}
              </p>
            )}
          </div>
        )}
      </div>
    </Link>
  );
}
